<template>
  <client-only>
    <uikit-headband
      v-if="isAdmin"
      type="information"
      class="u-text-center"
    >
      ADMIN:
      <uikit-button
        type="link"
        @click="handleManageTags"
      >
        Gérer
      </uikit-button>
      les tags de cette annonce pour la retrouver dans des recherches
    </uikit-headband>
    <uikit-headband
      v-if="iAmAuthor"
      type="information"
      class="u-text-center"
    >
      Une modification à apporter ?
      <br class="u-hide-desktop">
      <a
        class="cta-link"
        :href="editAdUrl"
        target="_blank"
      >
        Modifier mon annonce
      </a>
    </uikit-headband>
  </client-only>
  <div class="announce-page">
    <announces-no-announce v-if="noAnnounce" />
    <div
      v-else-if="announce"
      class="announce"
      :class="{ inactive }"
    >
      <section
        class="pictures"
        :class="{ inactive }"
      >
        <div
          v-for="(picture, index) in pictures"
          :key="'picture-' + index"
          class="picture"
          :class="{ 'main-picture': index === 0 }"
        >
          <img
            :src="picture"
            :alt="'announce-picture-' + index"
            @click="handleOpenPicture(index)"
          >
          <div
            v-if="index === 0"
            class="actions"
          >
            <NuxtLink
              class="u-cursor-pointer"
              :to="backUrl"
              :external="typeof backUrl === 'string'"
            >
              <uikit-back-icon />
            </NuxtLink>
            <div
              class="favorite-cta"
              @click.prevent.stop="handleFavorite"
            >
              <uikit-button type="link">
                <template
                  v-if="tubV2FavoritesAndSearchesEnable"
                  #icon
                >
                  <uikit-heart-icon :class="{ active: isFavorite }" />
                </template>
              </uikit-button>
            </div>
          </div>
          <uikit-button
            v-if="index === 0 && picturesCount > 3 && !inactive"
            class="button-see-more"
            type="secondary"
            @click="handleOpenPicture"
          >
            Voir les {{ picturesCount }} photos
          </uikit-button>
          <div
            v-if="index === 0 && inactive"
            class="inactive-banner"
          >
            Non
            <br>
            disponible
          </div>
        </div>
        <client-only>
          <lightgallery
            :settings="{
              speed: 500,
              dynamic: true,
              dynamicEl: fullScreenPictures,
              plugins,
              download: false,
              showCloseIcon: true,
              mobileSettings: { controls: false, showCloseIcon: true, download: false },
            }"
            :on-init="handleInitGallery"
          />
        </client-only>
      </section>

      <section class="content">
        <div class="ordered-blocks">
          <div class="block">
            <h1
              class="title"
              v-html="title"
            />
            <div v-if="hasVirtualTour">
              <a
                :href="announce.virtualTourUrl"
                class="cta-link u-weight-semi-bold u-dis-flex"
                target="_blank"
              >
                <uikit-camera-retro-icon class="u-mr-sm" />
                Visite virtuelle
              </a>
            </div>
          </div>

          <div class="block wide">
            <div class="top-information">
              <div class="performance">
                <div class="value">
                  {{ announce.rendement }}%
                  <uikit-help class="help-white u-dis-inline-b u-align-middle u-fill-white">
                    Le rendement brut inclu le prix du logement et le montant des travaux.
                    <template v-if="announce.rawRendement">
                      Le rendement brut sans travaux est de : {{ announce.rawRendement }}%
                    </template>
                  </uikit-help>
                </div>
                <div class="text">
                  Rendement brut
                </div>
              </div>
              <div class="price">
                <div class="line">
                  <span class="price-value">{{ formatPrice(announce.prix) }}</span>
                  <span class="square-meter-price-value">• {{ formatPrice(announce.prixm2) }}/m2</span>
                </div>
                <div class="line">
                  <span class="text">Prix de vente</span>
                  <uikit-help class="u-dis-inline-b u-align-middle">
                    <div class="u-weight-semi-bold u-color-gray-100">
                      <template v-if="announce.agencyFeesPaidByBuyer">
                        <div class="u-mb-md">
                          Frais d’agence:
                          <span class="u-color-primary-100">{{ formatPrice(announce.agencyFees) }}</span>
                        </div>
                        <div class="u-mb-md">
                          Prix de vente hors frais d’agence:
                          <span class="u-color-primary-100">{{ formatPrice(announce.netSellingPrice) }}</span>
                        </div>
                      </template>
                      <div
                        v-else
                        class="u-mb-md"
                      >
                        Frais d"agence à la charge du vendeur
                      </div>
                      <div
                        v-if="displayOldPrice"
                        class="u-mb-md"
                      >
                        Ancien prix:
                        <span class="u-color-primary-100">{{ formatPrice(announce.oldPrice) }}</span>
                      </div>
                    </div>
                  </uikit-help>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="separator" />

        <div class="block">
          <h2 class="block-title">
            Projet locatif
          </h2>
          <uikit-tag-list
            :tags="recommendedStrategies"
            class="u-mb-xl"
          />
          <announces-indicators :indicators="rentalProject">
            <template #data="{ indicator }">
              <span v-if="indicator.name === 'netPerformance'">
                <NuxtLink
                  no-prefetch
                  :to="simulatorLink"
                >
                  <uikit-button
                    type="link"
                    class="auto-height"
                  >
                    Calculer
                  </uikit-button>
                </NuxtLink>
              </span>
              <span v-else>{{ indicator.value }}</span>
            </template>
          </announces-indicators>
        </div>

        <div class="separator" />

        <div class="block">
          <h2 class="block-title">
            Description
          </h2>
          <announces-text-with-read-more
            class="description"
            :text="announce.description"
          />
        </div>

        <div class="separator" />

        <div
          v-if="hasRenovations"
          class="block"
        >
          <h2 class="block-title">
            Détails des travaux
          </h2>
          <announces-indicators
            v-if="renovationIndicators.length"
            :indicators="renovationIndicators"
          />
          <announces-text-with-read-more
            v-if="announce?.travauxDescription !== ''"
            class="description"
            :text="announce.travauxDescription"
          />
        </div>

        <div
          v-if="hasRenovations"
          class="separator"
        />

        <div
          v-if="hasLocationDescription"
          class="block"
        >
          <h2 class="block-title">
            Emplacement
          </h2>
          <announces-text-with-read-more
            class="description"
            :text="announce.locationDescription"
          />
        </div>

        <div
          v-if="hasLocationDescription"
          class="separator"
        />

        <div class="block">
          <h2 class="block-title">
            Caractéristiques ({{ characteristics.length }})
          </h2>
          <announces-indicators :indicators="characteristics" />
        </div>

        <div class="separator" />

        <div class="block">
          <h2 class="block-title">
            Chauffage et diagnostics
          </h2>
          <template v-if="heating.length === 0">
            informations non communiquées
          </template>
          <announces-indicators
            v-else
            :indicators="heating"
          />
        </div>

        <div class="separator" />

        <div class="block publisher-name">
          Annonce publiée le {{ creationDate }} par {{ publisherIdentity }}
        </div>
      </section>

      <section class="contact-form">
        <div class="form-contact">
          <template v-if="inactive">
            <announces-not-active />
          </template>
          <template v-else>
            <div class="header">
              <div class="agency">
                <img
                  v-if="hasAgency"
                  width="48"
                  height="48"
                  class="logo"
                  :alt="`Logo de ${announce.nomagence}`"
                  :src="agency.logo"
                >
                <div class="metas">
                  <div class="name">
                    {{ announce.nomagence }}
                  </div>
                  <announces-form-phone-contact
                    :on-submit-and-reveal-handler="handlePhoneContactFormSubmit"
                  />
                </div>
              </div>
            </div>
            <a
              v-if="hasAgency"
              :href="agencyPath"
              target="_blank"
              class="agency-link"
            >
              En savoir + sur l’agence
              <uikit-redirect-icon class="u-ml-sm icon" />
            </a>
            <div class="separator u-hide-mobile" />
            <uikit-button
              v-if="!hasBeenContacted"
              class="u-hide-mobile"
              type="primary"
              @click="handleSendMessage"
            >
              Contacter
            </uikit-button>
            <template v-else>
              <div class="success">
                Votre demande a bien été prise en compte.
              </div>
            </template>
          </template>
        </div>
      </section>

      <section class="cards-links">
        <cards-card-plugin class="card" />
        <cards-card-alerts-app class="card" />
      </section>

      <div class="mobile-contact-cta u-hide-desktop">
        <uikit-button
          class="u-w-full"
          type="primary"
          @click="handleSendMessage"
        >
          Contacter
        </uikit-button>
      </div>
    </div>

    <section class="annonces-wrapper">
      <LazyAnnonces
        class="annonces"
        :city="announce.ville"
        :postal-code="announce.cp"
        :hide-announces-with-id="[announce.id]"
        :column="isMobile"
      >
        <template #title>
          <div class="annonces-title">
            Découvrir d'autres
            <span>annonces exclusives</span>
          </div>
        </template>
      </LazyAnnonces>
    </section>
  </div>
</template>

<script setup>
import {
  UikitBallotDuotoneIcon,
  UikitButton,
  UikitHouseBlankDuotoneIcon,
  UikitHouseBuildingIcon,
  UikitHouseUserDuotoneIcon,
  UikitMaximizeDuotoneIcon,
  UikitMoneyBillsIcon,
  UikitMoneyCheckDuotoneIcon,
  UikitCameraRetroIcon,
  UikitHouseCrackedIcon,
  UikitMeublesIcon,
  UikitHelp,
  UikitTagList,
  useScreenSizeStore,
  UikitDryerIcon,
  UikitBatteryIcon,
  UikitGasEmissionIcon,
  UikitMoneyCheckSignIcon,
  UikitStreetViewIcon,
  UikitSinkBathroomIcon,
  UikitToiletsIcon,
  UikitBuildingBricksIcon,
  UikitBuildingBricksPeopleIcon,
  UikitBuildingBricksMoneyIcon,
  UikitCaveIcon,
  UikitCarGarageIcon,
  UikitBalconIcon,
  UikitTerrasseIcon,
  UikitTreesIcon,
  UikitBuildingIcon,
  UikitStairsIcon,
  UikitElevatorIcon,
  UikitParkingIcon,
  UikitGarageIcon,
  UikitGrenierIcon,
  UikitVerandaIcon,
  UikitHeadband,
  UikitChambersIcon,
  UikitYearConstructionIcon,
  UikitRedirectIcon,
  useDrawerStore,
  UikitBackIcon,
  dateFormat,
  UikitHeartIcon,
} from '@hz/ui-kit'
import DrawerFormContact from '@/components/Announces/DrawerContact.vue'

import Lightgallery from 'lightgallery/vue'
import lgthumbnail from 'lightgallery/plugins/thumbnail/lg-thumbnail.min.js'
import lgzoom from 'lightgallery/plugins/zoom/lg-zoom.min.js'
import '~/assets/css/components/lightgallery.css'
import { useAuthStore } from '@/stores/auth'
import { cleanHtml } from '../../../helpers/clean-html'
import { realEstateAdRentStrategyOptions } from '../../../helpers/real-estates'
import DrawerTubTagging from '../../../components/Tub/DrawerTubTagging.vue'
import { useTubStore } from '../../../stores/tub'
import { useTubApi } from '../../../composables/tub-api'
import { useFeaturesStore } from '~/stores/features.js'

const plugins = [lgthumbnail, lgzoom]

const authStore = useAuthStore()
const tubStore = useTubStore()
const config = useRuntimeConfig()

const route = useRoute()
const gallery = ref(null)
const noAnnounce = ref(false)
const hasBeenContacted = ref(false)
const isFavorite = ref(false)

const tubV2FavoritesAndSearchesEnable = computed(() => useFeaturesStore().tubV2FavoritesAndSearchesEnable)

function isClient() {
  return typeof process === 'undefined' || import.meta.client
}

const isAdmin = computed(() => useAuthStore().isAdmin)

const backUrl = computed(() => {
  if (isClient()) {
    if (typeof window.history.state.back === 'string') {
      if (window.history.state.back.startsWith('/biens-rentables')) {
        const back = new URL('http://localhost' + window.history.state.back)
        const query = {}
        const entries = back.searchParams.entries()
        for (const [key, value] of entries) {
          query[key] = value
        }

        if (back.pathname === '/biens-rentables') {
          return { name: 'biens-rentables', query }
        } else if (back.pathname === '/biens-rentables/search') {
          return { name: 'biens-rentables-custom-search', query }
        } else {
          const parts = back.pathname.split('/')
          const numberOfSlash = parts.length - 1

          if (numberOfSlash === 2) {
            return { name: 'biens-rentables-category', query, params: { category: parts[2] } }
          } else if (numberOfSlash === 4) {
            return {
              name: 'biens-rentables-category-facet',
              query,
              params: { category: parts[2], city: parts[3], postalCode: parts[4] },
            }
          } else {
            return window.history.state.back
          }
        }
      } else if (window.history.state.back.startsWith('/preview/')) {
        return { name: 'biens-rentables' }
      } else {
        return window.history.state.back
      }
    } else {
      return { name: 'biens-rentables' }
    }
  } else {
    return { name: 'biens-rentables' }
  }
})

const inactive = computed(
  () => !announce.value.active || new Date(announce.value.expirationDate) < new Date(),
)
const hasAgency = computed(() => !!announce.value.agency)
const agency = computed(() => announce.value.agency)
const agencyPath = computed(() => {
  let cp = `${agency.value.identity.postalCode}`

  if (cp.length === 4) {
    cp = `0${cp}`
  }

  const city = encodeURIComponent(agency.value.identity.city).replace(/[!'()*]/g, escape)

  return `/ou-investir/villes/${city}/${cp}/agences/${agency.value.slug}/${agency.value.id}`
})
const adImages = computed(() =>
  announce.value.images_url.length
    ? announce.value.images_url
    : ['https://placehold.co/200?text=Horiz.io&font=open-sans'],
)
const pictures = computed(() => adImages.value.slice(0, 3))
const picturesCount = computed(() => adImages.value.length)
const fullScreenPictures = computed(() =>
  adImages.value.map((picture) => ({
    src: picture,
    thumb: picture,
  })),
)
const formattedType = computed(
  () => announce.value.type.charAt(0).toUpperCase() + announce.value.type.slice(1),
)
const title = computed(() => {
  if (announce.value.nbpieces === 0) {
    return `${formattedType.value} (${announce.value.surface}m<sup>2</sup>) à ${announce.value.ville} (${announce.value.cp})`
  } else {
    return `${formattedType.value} ${announce.value.nbpieces} pièce${
      announce.value.nbpieces > 1 ? 's' : ''
    } (${announce.value.surface}m<sup>2</sup>) à ${announce.value.ville} (${announce.value.cp})`
  }
})
const hasVirtualTour = computed(() => !!announce.value.virtualTourUrl)
const recommendedStrategies = computed(() => {
  const strategies = []
  const recommendedStrategies = announce.value.recommendedStrategies ?? []

  recommendedStrategies.forEach((strategy) => {
    const current = realEstateAdRentStrategyOptions.find((option) => option.value === strategy)
    if (current) {
      strategies.push(current.label)
    }
  })

  return strategies.map((strategy) => ({
    label: strategy,
    type: 'default',
  }))
})

const iAmAuthor = computed(() => authStore.user?.id?.toString() === announce.value.userId)
const editAdUrl = computed(() => `${config.public.appBaseUrl}/real-estate-ads/edit/${announce.value.id}`)

const rentalProject = computed(() => {
  const indicators = [
    {
      name: 'netPerformance',
      icon: UikitMoneyBillsIcon,
      label: 'Rendement net',
      value: formattedState.value,
    },
    {
      icon: UikitHouseBlankDuotoneIcon,
      label: 'Etat du bien',
      value: formattedState.value,
    },
    {
      icon: UikitMoneyCheckSignIcon,
      label: 'Prix',
      value: formatPrice(announce.value.prix),
    },
    {
      icon: UikitHouseUserDuotoneIcon,
      label: 'Déjà loué',
      value: formattedAlreadyRented.value,
    },
    {
      icon: UikitMoneyCheckDuotoneIcon,
      label: 'Loyer',
      value: formatPrice(announce.value.loyer),
    },
  ]

  if (announce.value?.travaux > 0) {
    indicators.push({
      icon: UikitHouseCrackedIcon,
      label: 'Montant des travaux',
      value: formatPrice(announce.value.travaux),
    })
  }

  if (announce.value?.totalMeubles > 0) {
    indicators.push({
      icon: UikitMeublesIcon,
      label: 'Montant des meubles',
      value: formatPrice(announce.value.totalMeubles),
    })
  }

  return indicators
})

const renovationIndicators = computed(() => {
  const indicators = []

  if (announce.value?.travaux > 0) {
    indicators.push({
      icon: UikitHouseCrackedIcon,
      label: 'Montant des travaux',
      value: formatPrice(announce.value.travaux),
    })
  }

  return indicators
})

const characteristics = computed(() => {
  const indicators = [
    {
      icon: UikitHouseBuildingIcon,
      label: 'Type de bien',
      value: formattedType.value,
    },
    {
      icon: UikitMaximizeDuotoneIcon,
      label: 'Surface habitable (en m2)',
      value: announce.value.surface,
    },
  ]

  if (announce.value.nbpieces > 0) {
    indicators.push({
      icon: UikitBallotDuotoneIcon,
      label: 'Nombre de pièces',
      value: announce.value.nbpieces,
    })
  }

  indicators.push(
    {
      icon: UikitHouseBlankDuotoneIcon,
      label: 'Etat du bien',
      value: formattedState.value,
    },
    {
      icon: UikitHouseUserDuotoneIcon,
      label: 'Déjà loué',
      value: formattedAlreadyRented.value,
    },
    {
      icon: UikitMoneyCheckDuotoneIcon,
      label: 'Loyer',
      value: formatPrice(announce.value.loyer),
    },
  )

  if (isDefined(announce.value.yearOfConstruction)) {
    indicators.push({
      icon: UikitYearConstructionIcon,
      label: 'Année de construction',
      value: announce.value.yearOfConstruction,
    })
  }

  if (isDefined(announce.value.floor)) {
    indicators.push({
      icon: UikitBuildingIcon,
      label: 'Etage du bien',
      value: announce.value.floor,
    })
  }

  if (isDefined(announce.value.nbFloor)) {
    indicators.push({
      icon: UikitStairsIcon,
      label: "Nombre d'étages",
      value: announce.value.nbFloor,
    })
  }

  if (isDefined(announce.value.elevator)) {
    indicators.push({
      icon: UikitElevatorIcon,
      label: 'Ascenseur',
      value: formatYesNo(announce.value.elevator),
    })
  }

  if (isDefined(announce.value.hasParking)) {
    const indicator = {
      icon: UikitParkingIcon,
      label: 'Parking',
      value: formatYesNo(announce.value.hasParking),
    }

    if (announce.value.surfaceParking) {
      indicator.value += ` - ${announce.value.surfaceParking}m2`
    }

    indicators.push(indicator)
  }

  if (isDefined(announce.value.hasBoxe)) {
    const indicator = {
      icon: UikitCarGarageIcon,
      label: 'Box',
      value: formatYesNo(announce.value.hasBoxe),
    }

    if (announce.value.surfaceBoxe) {
      indicator.value += ` - ${announce.value.surfaceBoxe}m2`
    }

    indicators.push(indicator)
  }

  if (isDefined(announce.value.hasGarage)) {
    const indicator = {
      icon: UikitGarageIcon,
      label: 'Garage',
      value: formatYesNo(announce.value.hasGarage),
    }

    if (announce.value.surfaceGarage) {
      indicator.value += ` - ${announce.value.surfaceGarage}m2`
    }

    indicators.push(indicator)
  }

  if (isDefined(announce.value.hasCave)) {
    const indicator = {
      icon: UikitCaveIcon,
      label: 'Cave',
      value: formatYesNo(announce.value.hasCave),
    }

    if (announce.value.surfaceCave) {
      indicator.value += ` - ${announce.value.surfaceCave}m2`
    }

    indicators.push(indicator)
  }

  if (isDefined(announce.value.hasVeranda)) {
    const indicator = {
      icon: UikitVerandaIcon,
      label: 'Véranda',
      value: formatYesNo(announce.value.hasVeranda),
    }

    if (announce.value.surfaceVeranda) {
      indicator.value += ` - ${announce.value.surfaceVeranda}m2`
    }

    indicators.push(indicator)
  }

  if (isDefined(announce.value.hasGrenier)) {
    const indicator = {
      icon: UikitGrenierIcon,
      label: 'Grenier',
      value: formatYesNo(announce.value.hasGrenier),
    }

    if (announce.value.surfaceGrenier) {
      indicator.value += ` - ${announce.value.surfaceGrenier}m2`
    }

    indicators.push(indicator)
  }

  if (isDefined(announce.value.hasJardin)) {
    const indicator = {
      icon: UikitTreesIcon,
      label: 'Jardin',
      value: formatYesNo(announce.value.hasJardin),
    }

    if (announce.value.surfaceJardin) {
      indicator.value += ` - ${announce.value.surfaceJardin}m2`
    }

    indicators.push(indicator)
  }

  if (isDefined(announce.value.hasBalcon)) {
    const indicator = {
      icon: UikitBalconIcon,
      label: 'Balcon',
      value: formatYesNo(announce.value.hasBalcon),
    }

    if (announce.value.surfaceBalcon) {
      indicator.value += ` - ${announce.value.surfaceBalcon}m2`
    }

    indicators.push(indicator)
  }

  if (isDefined(announce.value.hasTerrasse)) {
    const indicator = {
      icon: UikitTerrasseIcon,
      label: 'Terrasse',
      value: formatYesNo(announce.value.hasTerrasse),
    }

    if (announce.value.surfaceTerrasse) {
      indicator.value += ` - ${announce.value.surfaceTerrasse}m2`
    }

    indicators.push(indicator)
  }

  if (isDefined(announce.value.guarding)) {
    indicators.push({
      icon: UikitStreetViewIcon,
      label: 'Gardiennage',
      value: formatYesNo(announce.value.guarding),
    })
  }

  if (isDefined(announce.value.nbChambers)) {
    indicators.push({
      icon: UikitChambersIcon,
      label: 'Chambre(s)',
      value: announce.value.nbChambers,
    })
  }

  if (isDefined(announce.value.nbBathRooms)) {
    indicators.push({
      icon: UikitSinkBathroomIcon,
      label: 'Salle(s) d’eau',
      value: announce.value.nbBathRooms,
    })
  }

  if (isDefined(announce.value.nbToilets)) {
    indicators.push({
      icon: UikitToiletsIcon,
      label: 'Toilette(s)',
      value: announce.value.nbToilets,
    })
  }

  if (isDefined(announce.value.coOwnership)) {
    indicators.push({
      icon: UikitBuildingBricksIcon,
      label: 'Copropriété',
      value: formatYesNo(announce.value.coOwnership),
    })
  }

  if (isDefined(announce.value.nbLots)) {
    indicators.push({
      icon: UikitBuildingBricksPeopleIcon,
      label: 'Nombre de lots',
      value: `${announce.value.nbLots} lots`,
    })
  }

  if (isDefined(announce.value.ownerCharges)) {
    indicators.push({
      icon: UikitBuildingBricksMoneyIcon,
      label: 'Charges propriétaire',
      value: `${formatPrice(announce.value.ownerCharges)}/an`,
    })
  }

  if (isDefined(announce.value.tf)) {
    indicators.push({
      icon: UikitMoneyBillsIcon,
      label: 'Taxe foncière',
      value: formatPrice(announce.value.tf),
    })
  }

  return indicators
})

const heating = computed(() => {
  const indicators = []

  if (announce.value.heating) {
    indicators.push({
      icon: UikitDryerIcon,
      label: 'Type de chauffage',
      value: announce.value.heating === 'individuel' ? 'Individuel' : 'Collectif',
    })
  }

  if (announce.value.energyClass) {
    indicators.push({
      icon: UikitBatteryIcon,
      label: 'Classe énergie',
      value: announce.value.energyClass === 'vierge' ? 'Vierge' : announce.value.energyClass.toUpperCase(),
    })
  }

  if (announce.value.ges) {
    indicators.push({
      icon: UikitGasEmissionIcon,
      label: 'GES',
      value: announce.value.ges === 'vierge' ? 'Vierge' : announce.value.ges.toUpperCase(),
    })
  }

  return indicators
})

const creationDate = computed(() => {
  return dateFormat(announce.value?.creation_date ?? new Date(), 'dd mmmm yyyy')
})

const publisherIdentity = computed(() => {
  let identity = announce.value.nomagence
  if (isDefined(announce.value.siren)) {
    identity += ` | ${announce.value.siren}`
  }

  return identity
})

const hasRenovations = computed(
  () => announce.value?.travaux > 0 || announce.value?.travauxDescription?.length > 0,
)
const hasLocationDescription = computed(() => isDefined(announce.value?.locationDescription))
const formattedState = computed(() => (announce.value.neuf ? 'Neuf' : 'Ancien'))
const formattedAlreadyRented = computed(() => (announce.value.locataireInside ? 'Oui' : 'Non'))
const displayOldPrice = computed(() => announce.value.oldPrice !== announce.value.prix)

const simulatorLink = computed(() => announce.value.simulationUrl)

const formatPrice = (value) => {
  return typeof value !== 'undefined' ? `${value.toLocaleString()} €` : ''
}

const formatYesNo = (value) => {
  return typeof value !== 'undefined' ? (value ? 'Oui' : 'Non') : ''
}

const isDefined = (value) => {
  return typeof value !== 'undefined' && value !== null && value !== ''
}

const handleInitGallery = (detail) => {
  gallery.value = detail.instance
}

const handleOpenPicture = (index = 0) => {
  gallery.value.openGallery(index)
}

const { data: announce, error } = await useAsyncData(() => useAdsApi().getAnnounceById(route.params.id), {
  default: () => null,
})

const isMobile = computed(() => useScreenSizeStore().isMobileWidth)

const handleManageTags = async () => {
  useDrawerStore().addDrawer({
    id: 'tub-drawer-tagging',
    component: shallowRef(DrawerTubTagging),
    props: {
      realEstateOfferId: announce.value.id,
    },
  })
}

const handleFavorite = async () => {
  if (isFavorite.value) {
    const confirmed = await tubStore.removeToFavorites(announce.value.id)
    if (confirmed) {
      isFavorite.value = false
    }
  } else {
    isFavorite.value = true
    await tubStore.addToFavorites(announce.value.id)
  }
}

// Head metas
const pageUrl = `${config.public.hpBaseUrl}${route.path}`
const pageTitle = `${cleanHtml(title.value)} | Horiz.io`
const description = `${cleanHtml(announce.value.description).substring(0, 200)}...`
const featureImageUrl = adImages.value[0]
const featureImageAltText = `Photo (${cleanHtml(title.value)})`

useHead({
  title: pageTitle,
  meta: [
    {
      hid: 'robots',
      name: 'robots',
      content: 'noindex',
    },
    {
      hid: 'description',
      name: 'description',
      content: description,
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: cleanHtml(title.value),
    },
    {
      hid: 'og:description',
      property: 'og:description',
      content: description,
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: featureImageUrl,
    },
    {
      hid: 'og:url',
      property: 'og:url',
      content: pageUrl,
    },
    {
      hid: 'og:site_name',
      property: 'og:site_name',
      content: 'Horiz.io',
    },
    {
      hid: 'og:type',
      property: 'og:type',
      content: 'article',
    },
    {
      hid: 'twitter:card',
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      hid: 'twitter:image:alt',
      name: 'twitter:image:alt',
      content: featureImageAltText,
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: pageUrl,
    },
  ],
})

const breadcrumb = [
  {
    '@type': 'ListItem',
    position: 1,
    name: 'accueil',
    item: config.public.hpBaseUrl,
  },
  {
    '@type': 'ListItem',
    position: 2,
    name: cleanHtml(title.value),
    item: pageUrl,
  },
]

useJsonld([
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Horiz',
    alternateName: 'Rendement Locatif',
    url: config.public.hpBaseUrl,
    logo: `${config.public.hpBaseUrl}/imgs/logo.png`,
  },
  {
    '@context': 'https://schema.org/',
    '@type': 'WebSite',
    url: config.public.hpBaseUrl,
    name: 'Horiz',
    alternateName: 'Rendement locatif',
  },
  {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumb,
  },
  {
    '@context': 'https://schema.org/',
    '@type': 'WebPage',
    name: pageTitle,
    speakable: {
      '@type': 'SpeakableSpecification',
      xpath: ['/html/head/title', "/html/head/meta[@name='description']/@content"],
    },
    url: pageUrl,
  },
])

const handlePhoneContactFormSubmit = async (reCaptchaToken) => {
  const res = await useAdsApi().getAdPhone(announce.value.id, {
    reCaptchaToken: reCaptchaToken,
  })

  await useTracking().trackGA4Event('lead_annonce')

  return res.phone
}

const handleContactFormSubmit = async (contactFormData) => {
  // Return empty response
  await useAdsApi().sendAdContactForm(announce.value.id, contactFormData)
  await useTracking().trackGA4Event('lead_annonce')
}

const handleSendMessageSuccess = () => {
  hasBeenContacted.value = true
}

const handleSendMessage = async () => {
  useDrawerStore().addDrawer({
    id: 'drawer-ad-agency-contact',
    component: shallowRef(DrawerFormContact),
    props: {
      onSuccess: handleSendMessageSuccess,
      handleSubmitContactForm: handleContactFormSubmit,
      agencyName: announce.value.nomagence,
      agencyLogoUrl: hasAgency.value ? agency.value.logo : null,
      handleDisplayPhoneNumber: handlePhoneContactFormSubmit,
    },
  })
}

onMounted(async () => {
  if (import.meta.client) {
    useHead({
      script: [
        {
          src: `https://www.google.com/recaptcha/enterprise.js?render=${config.public.adReCaptchaSiteKey}`,
          async: true,
          defer: true,
          tagPosition: 'head',
        },
      ],
    })
  }

  if (error.value) {
    noAnnounce.value = true
  }

  if (announce.value) {
    if (import.meta.client) {
      await tubStore.flagAsSeen(announce.value.id)
    }

    const offerFromServer = await useTubApi().getById(announce.value.id)
    isFavorite.value = offerFromServer.favorite

    const trackingAttributes = {
      _id: announce.value.id,
      userId: announce.value.userId,
      cp: announce.value.cp,
      prix: announce.value.prix,
      nbpieces: announce.value.nbpieces,
      neuf: announce.value.neuf,
      loyer: announce.value.loyer,
      travaux: announce.value.travaux,
      surface: announce.value.surface,
      rendement: announce.value.rendement,
      agencyReference: announce.value.agencyReference,
      active: announce.value.active,
      nomagence: announce.value.nomagence,
      creation_date: announce.value.creation_date,
      expirationDate: announce.value.expirationDate,
    }
    await useTracking().trackAfterMounted("[Annonces BtoB] Visite d'une page annonce", trackingAttributes)
    await useTracking().trackGA4EventAfterMounted('page_annonce_vue')
  }
})
</script>

<style lang="less">
.lg-outer {
  .lg-thumb-item.active,
  .lg-thumb-item:hover {
    border-color: var(--ds-color-primary-100);
  }
}
</style>

<style lang="less" scoped>
//-------- contact form

.agency-link {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  background-color: var(--ds-color-primary-25);
  color: var(--ds-color-primary-100);
  font-size: 16px;
  font-style: normal;
  font-weight: var(--ds-weight-semi-bold);

  .icon {
    width: 16px;
    height: 16px;
  }
}

.form-contact {
  width: 100%;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  border-radius: 8px;
  border: 1px solid var(--ds-color-gray-10);
  background: var(--ds-color-white-100);

  .header {
    .agency {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 18px;

      .logo {
        align-self: flex-start;
        width: 48px;
        height: 48px;
        border-radius: 50%;
      }
      .metas {
        display: flex;
        flex-direction: column;
      }
      .name {
        font-size: 18px;
        font-weight: var(--ds-weight-semi-bold);
      }
    }
  }
}

.mobile-contact-cta {
  padding: 20px 32px;
  border-top: 1px solid var(--ds-color-gray-10);
  background: var(--ds-color-white-100);
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

@media @bp-lg {
  .form-contact {
    max-width: 420px;
  }
}

//--------

:deep(.help-white) {
  svg {
    fill: var(--ds-color-white-100);
  }
}

.auto-height {
  height: auto;
}

.announce-page {
  position: relative;

  @media @bp-lg {
    &:after {
      content: '';
      position: absolute;
      background-color: #f7f8ff;
      width: 100%;
      height: 412px;
      top: 0;
      left: 0;
      z-index: -1;
    }
  }
}

.announce {
  width: 100%;
  max-width: 1700px;
  padding: 0 15px;
  margin: auto;
  display: grid;
  grid-template-areas:
    'pictures'
    'content'
    'contact-form'
    'cards-links';
}

.pictures {
  grid-area: pictures;
  position: relative;
  height: 300px;
  margin: 0 -15px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .picture {
    overflow: hidden;
    height: 100%;

    &:hover {
      cursor: pointer;
    }
  }

  .picture:not(:first-child) {
    display: none;
  }

  .button-see-more {
    position: absolute;
    bottom: 20px;
    right: 20px;
  }

  .inactive-banner {
    background: var(--ds-color-primary-100);
    position: absolute;
    top: 0;
    right: -80px;
    color: white;
    text-transform: uppercase;
    padding: 20px 80px;
    text-align: center;
    transform: rotate(45deg);
    font-weight: var(--ds-weight-semi-bold);
  }

  &.inactive {
    .picture > img {
      filter: grayscale(1);
    }
  }

  .actions {
    position: absolute;
    top: 20px;
    left: 20px;
    right: 20px;
    display: flex;
    justify-content: space-between;

    .favorite-cta {
      display: flex;

      :deep(button) {
        height: auto;
      }

      :deep(.icon) {
        height: 24px;
        width: 24px;
        transition: all 0.25s cubic-bezier(0.23, 1, 0.32, 1);

        justify-content: center;
        align-items: center;

        svg {
          height: 100%;
          width: 100%;
          transition: all 1s cubic-bezier(0.23, 1, 0.32, 1);

          path {
            transition: all 1s cubic-bezier(0.23, 1, 0.32, 1);
            fill: var(--ds-color-gray-25);
            stroke: var(--ds-color-gray-50);
          }

          &:not(.active):hover {
            path {
              fill: var(--ds-color-error-25);
              stroke: var(--ds-color-error-100);
            }
          }

          &.active {
            animation: heart-beat 1s;
            animation-iteration-count: 1;
            path {
              fill: var(--ds-color-error-100);
              stroke: var(--ds-color-error-100);
            }
          }
        }
      }
    }
  }
}

.content {
  grid-area: content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .ordered-blocks {
    display: flex;
    flex-direction: column-reverse;
    width: 100%;
  }

  .title {
    font-size: 26px;
    font-weight: var(--ds-weight-semi-bold);
    margin-bottom: 16px;
  }

  .top-information {
    display: flex;

    .performance,
    .price {
      display: flex;
      gap: 12px;
      flex-direction: column;
      padding: 12px 20px;
    }

    .performance {
      background-color: var(--ds-color-primary-100);

      .value {
        font-size: 24px;
        font-style: normal;
        font-weight: var(--ds-weight-semi-bold);
        color: var(--ds-color-white-100);
      }

      .text {
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        color: var(--ds-color-gray-25);
      }
    }

    .price {
      border: 1px solid var(--ds-color-gray-25);
      background: var(--ds-color-white-100);
      flex: 1;

      .price-value {
        color: var(--ds-color-gray-100);
        font-size: 24px;
        font-style: normal;
        font-weight: var(--ds-weight-semi-bold);
        margin-right: 4px;
      }
      .square-meter-price-value {
        color: var(--ds-color-gray-50);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
      }
      .text {
        color: var(--ds-color-gray-50);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
      }
    }
  }

  .block {
    width: 100%;
    margin-bottom: 32px;

    &.wide {
      width: calc(100% + 15px + 15px);
      margin-left: -15px;
      margin-right: -15px;
    }

    .block-title {
      color: var(--ds-color-primary-100);
      margin-bottom: 12px;
      font-size: 26px;
      font-weight: var(--ds-weight-semi-bold);
    }
  }

  .separator {
    width: 100%;
    height: 1px;
    background-color: var(--ds-color-gray-25);
    margin-bottom: 32px;
  }

  .publisher-name {
    color: var(--ds-color-gray-50);
    font-weight: 500;
  }
}

.contact-form {
  grid-area: contact-form;
}

.cards-links {
  grid-area: cards-links;
  padding: 32px 0 60px;

  .card {
    background: var(--ds-color-primary-25);
  }
}

.annonces-wrapper {
  background-color: #f7f8ff;
  padding-bottom: 32px;

  .annonces {
    padding: 48px 15px 0;
    width: 100%;
    max-width: 1700px;
    margin: auto;

    .annonces-title {
      font-size: 26px;
      font-weight: var(--ds-weight-semi-bold);
      margin-bottom: 20px;

      span {
        color: var(--ds-color-primary-100);
      }
    }
  }
}

@media @bp-lg {
  .announce {
    padding: 0 50px;
    grid-template-columns: auto minmax(auto, 420px);
    grid-column-gap: 58px;
    grid-template-areas:
      'pictures contact-form'
      'content contact-form'
      'cards-links contact-form';
  }

  .pictures {
    position: relative;
    display: grid;
    max-width: 750px;
    grid-template-columns: calc(66% - 20px) calc(33% - 20px);
    grid-template-rows: 140px 140px;
    grid-column-gap: 40px;
    grid-row-gap: 16px;
    padding: 52px 0 64px;
    height: auto;
    margin: 0;

    .main-picture {
      grid-row: ~'1 / span 2';
      position: relative;
    }

    .picture {
      display: block !important;
    }

    img {
      border-radius: 8px;
    }

    .actions {
      top: 20px;
      left: 20px;
      right: 20px;
    }
  }

  .content {
    .top-information {
      .price {
        flex: initial;
      }
    }

    .ordered-blocks {
      flex-direction: column;
    }

    .title {
      font-size: 34px;
    }

    .top-information {
      .performance,
      .price {
        width: auto;
      }

      .performance {
        .value {
          font-size: 26px;
        }
        .text {
          font-size: 16px;
        }
      }
      .price {
        .price-value {
          font-size: 26px;
        }
        .square-meter-price-value {
          font-size: 16px;
        }
        .text {
          font-size: 16px;
        }
      }
    }

    .block {
      &.wide {
        width: auto;
        margin-left: 0;
        margin-right: 0;
      }

      .block-title {
        margin-bottom: 24px;
      }
    }
  }

  .contact-form {
    margin-top: 32px;

    :deep(.form-contact) {
      position: sticky;
      top: 80px;
    }
  }

  .cards-links {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 42px;
  }

  .annonces-wrapper {
    .annonces {
      padding: 48px 50px 0;

      :deep(.swipe-container) {
        width: calc(224px * 4 + 22px * 3);
      }
    }
  }
}
</style>
